import { Shield, Lock, BadgeCheck } from "lucide-react";

// Razorpay's official logomark from their public CDN (same asset their docs
// recommend partners embed). Using their hosted SVG avoids stale local
// copies if they refresh their brand identity.
const RAZORPAY_LOGO_URL = "https://razorpay.com/assets/razorpay-logo.svg";

/**
 * RazorpayTrustBadge
 * ─────────────────────────────────────────────────────────────────────
 * Customer-facing trust certification card showing that payments on
 * Travelsate are processed through Razorpay's PCI-DSS Level 1 payment
 * gateway. Used in three places to maximise the trust signal customers
 * see across their journey:
 *   1. LandingPage — above the footer ("Trust & Safety" strip)
 *   2. AboutPage   — in the Contact section, beside the social pills
 *   3. BookingPage — at the top of Step-6 Pay, replacing the tiny inline
 *      strip we previously shipped
 *
 * `variant="full"`   → large card with logo, headline, sub-text, and the
 *                      4-pill trust row (PCI-DSS · RBI · 256-bit SSL · 3DS).
 * `variant="compact"`→ horizontal strip with logo + sub-text + pills, fits
 *                      inside the Step-6 payment summary card.
 */
export function RazorpayTrustBadge({ variant = "full", className = "" }) {
  const pills = [
    { icon: BadgeCheck, label: "PCI-DSS Level 1" },
    { icon: Shield,     label: "RBI-approved PA" },
    { icon: Lock,       label: "256-bit SSL" },
    { icon: Shield,     label: "3DS Secure" },
  ];

  if (variant === "compact") {
    return (
      <div
        data-testid="razorpay-trust-badge-compact"
        className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 ${className}`}
      >
        <div className="flex items-center gap-2">
          <img
            src={RAZORPAY_LOGO_URL}
            alt="Razorpay"
            className="h-5 w-auto opacity-90 bg-white/95 px-1.5 py-0.5 rounded"
            loading="lazy"
            data-testid="razorpay-logo-compact"
          />
          <span className="text-white/75 text-xs font-medium tracking-wide">100% Secure Payments</span>
        </div>
        <span className="hidden sm:inline h-3.5 w-px bg-white/15" />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {pills.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1 text-[10px] text-white/55 tracking-wide">
              <Icon className="w-3 h-3 text-[#D4AF37]/80" />
              {label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="razorpay-trust-badge-full"
      className={`relative overflow-hidden rounded-xl border border-[#D4AF37]/15 bg-gradient-to-br from-[#D4AF37]/[0.05] via-transparent to-blue-500/[0.05] p-6 sm:p-7 ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7">
        {/* Razorpay logomark in a white pill so it reads as an "official
            badge" — same visual pattern customers see on bank statements. */}
        <div className="shrink-0 flex items-center justify-center bg-white rounded-lg px-4 py-3 shadow-md shadow-black/40">
          <img
            src={RAZORPAY_LOGO_URL}
            alt="Razorpay — secure payments"
            className="h-7 w-auto"
            loading="lazy"
            data-testid="razorpay-logo-full"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <BadgeCheck className="w-4 h-4 text-[#D4AF37]" />
            <h3 className="text-white font-medium text-sm tracking-wide">
              Payments secured by Razorpay
            </h3>
          </div>
          <p className="text-white/55 text-xs leading-relaxed mb-3.5 max-w-xl">
            Every transaction on Travelsate is processed through Razorpay's PCI-DSS Level 1 certified payment gateway — the same infrastructure trusted by IRCTC, Zomato, and Tata 1mg. We never see or store your card details.
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            {pills.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/10 text-[10.5px] text-white/70 tracking-wide"
              >
                <Icon className="w-3 h-3 text-[#D4AF37]" />
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RazorpayTrustBadge;
